import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, ArrowRight, CheckCircle2, HelpCircle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Conf = ({ s }) => s.confidence === "clear"
  ? <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#1F6B45] bg-[#E6F4EC] px-1.5 py-0.5" data-testid="legacy-conf-clear"><CheckCircle2 className="w-3 h-3" /> Clear match</span>
  : <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#8A6508] bg-[#FFF4DC] px-1.5 py-0.5" data-testid="legacy-conf-unsure"><HelpCircle className="w-3 h-3" /> Unsure</span>;

const Stat = ({ label, value, testId, tone = "" }) => (
  <div className="px-3 py-2 bg-[#FAFAF7] border border-[#E8ECE9]" data-testid={testId}>
    <div className={`text-xl font-serif leading-none ${tone}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-[#7D8C85] mt-1">{label}</div>
  </div>
);

// Read-only: live free-tier suppliers whose listings still carry sub-category values from the old category list.
export default function LegacySubcategoryReport({ refreshKey, onApplied }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [applying, setApplying] = useState(false);
  const load = () => {
    setError("");
    api.get("/admin/free-listings/legacy-report").then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || "Couldn't load the report"));
  };
  useEffect(load, [refreshKey]);
  const sm = data?.summary;
  const items = (data?.items || []).filter((r) => filter === "all" || (filter === "clear" ? r.clear_count > 0 : r.status === "unsure"));

  const apply = async () => {
    if (!sm?.would_apply) return;
    if (!window.confirm(`Apply clear matches for ${sm.would_apply} unclaimed supplier${sm.would_apply === 1 ? "" : "s"}?\n\nOnly "Clear match" values are changed. Claimed suppliers are skipped. Old values are kept as search tags. Unsure values are left for you.`)) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/admin/free-listings/legacy-report/apply");
      toast.success(`Applied — ${r.suppliers_updated} supplier${r.suppliers_updated === 1 ? "" : "s"} updated, ${r.values_resolved} value${r.values_resolved === 1 ? "" : "s"} re-homed, ${r.claimed_skipped} claimed skipped, ${r.unsure_left} unsure left for you`);
      load();
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-white border border-[#E3B23C]/60" data-testid="legacy-subcategory-report">
      <div className="p-4 border-b border-[#E8ECE9] flex flex-wrap items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-[#8A6508]" />
        <div className="font-serif text-lg text-[#11211A] flex-1">
          Old sub-category values{data && <span className="text-sm text-[#7D8C85]" data-testid="legacy-report-count"> ({data.count} supplier{data.count === 1 ? "" : "s"} · {data.value_count} value{data.value_count === 1 ? "" : "s"})</span>}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#7D8C85]">Review · only "Apply clear matches" changes data</span>
        <button type="button" onClick={load} className="btn-ghost text-xs" title="Refresh" data-testid="legacy-report-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {error && <div className="p-4 text-sm text-[#B35C4A]" data-testid="legacy-report-error">{error}</div>}
      {!error && !data && <div className="p-4 text-sm text-[#7D8C85]">Loading…</div>}
      {data && data.count === 0 && <div className="p-4 text-sm text-[#4A5550]" data-testid="legacy-report-empty">No live supplier still has sub-category values from the old category list.</div>}
      {sm && data.count > 0 && (
        <>
          <div className="px-4 pt-4 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="legacy-report-summary">
            <Stat label="With clear matches" value={sm.with_clear} testId="legacy-stat-with-clear" tone="text-[#1F6B45]" />
            <Stat label="All values clear" value={sm.fully_clear} testId="legacy-stat-fully-clear" />
            <Stat label="Unsure only · by hand" value={sm.unsure_only} testId="legacy-stat-unsure" tone="text-[#8A6508]" />
            <Stat label="Would be applied (unclaimed)" value={sm.would_apply} testId="legacy-stat-would-apply" />
            <Stat label="Claimed · would be skipped" value={sm.claimed_skipped} testId="legacy-stat-claimed-skipped" />
          </div>
          <div className="px-4 pt-3 pb-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[#7D8C85]">Show:</span>
            {[["all", "All"], ["clear", "Has clear matches"], ["unsure", "Unsure only"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2.5 py-1 border transition ${filter === k ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839]"}`} data-testid={`legacy-filter-${k}`}>{label}</button>
            ))}
            <span className="text-[#7D8C85] ml-auto italic">Old values are kept as search tags — nothing is deleted.</span>
            <button type="button" onClick={apply} disabled={applying || !sm.would_apply} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="legacy-apply-clear-btn">
              <Wand2 className="w-3.5 h-3.5" /> {applying ? "Applying…" : `Apply clear matches (${sm.would_apply})`}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-[#7D8C85] bg-[#FAFAF7]">
                <tr>
                  <th className="p-3">Supplier</th>
                  <th className="p-3">Old value</th>
                  <th className="p-3">Suggested current sub-categories</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => r.values.map((v, i) => (
                  <tr key={`${r.supplier_id}-${v.old_value}`} className="border-t border-[#E8ECE9] align-top" data-testid={`legacy-report-row-${r.supplier_id}-${v.old_value}`}>
                    {i === 0 && (
                      <td className="p-3 font-medium text-[#11211A]" rowSpan={r.values.length} data-testid={`legacy-report-supplier-${r.supplier_id}`}>
                        {r.company}
                        <div className="text-[10px] uppercase tracking-wider text-[#7D8C85] font-normal mt-0.5">{r.tier}{r.is_unclaimed ? " · unclaimed" : " · claimed — skipped"}</div>
                        <div className="mt-1" data-testid={`legacy-report-status-${r.supplier_id}`}>
                          {r.status === "clear" && <span className="text-[9px] uppercase tracking-wider font-semibold text-[#1F6B45]">All clear</span>}
                          {r.status === "mixed" && <span className="text-[9px] uppercase tracking-wider font-semibold text-[#4A5550]">{r.clear_count} clear · {r.unsure_count} unsure</span>}
                          {r.status === "unsure" && <span className="text-[9px] uppercase tracking-wider font-semibold text-[#8A6508]">Handle by hand</span>}
                        </div>
                      </td>
                    )}
                    <td className="p-3"><code className="text-xs bg-[#F0EFEA] px-1.5 py-0.5 text-[#4A5550] break-all">{v.old_value}</code></td>
                    <td className="p-3 space-y-1.5">
                      {v.suggestions.map((s) => (
                        <div key={s.slug} className="flex flex-wrap items-center gap-2" data-testid={`legacy-suggestion-${r.supplier_id}-${v.old_value}-${s.slug}`}>
                          <span className="inline-flex items-center gap-1.5 text-[#264839]"><ArrowRight className="w-3 h-3" /> {s.name}</span>
                          <Conf s={s} />
                          {s.already_assigned && <span className="text-[9px] uppercase tracking-wider text-[#7D8C85]">already assigned</span>}
                          <span className="text-[10px] text-[#9AA6A0]">from “{s.from_part}”</span>
                        </div>
                      ))}
                      {v.unmatched_parts.map((p) => (
                        <div key={p} className="flex flex-wrap items-center gap-2" data-testid={`legacy-unmatched-${r.supplier_id}-${v.old_value}-${p}`}>
                          <span className="text-[#B35C4A] italic">No match for “{p}”</span>
                          <Conf s={{ confidence: "unsure" }} />
                        </div>
                      ))}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
